import type { ReactionType } from "../reactions.ts";
import type { Comment, Post, PostReactions } from "../types.ts";

function withType(types: ReactionType[], type: ReactionType): ReactionType[] {
	return types.includes(type) ? types : [...types, type];
}

export function applyPostReaction(post: Post, type: ReactionType): Post {
	const { count, reacted, type: active, types } = post.reactions;

	if (reacted && active === type) {
		const nextCount = Math.max(0, count - 1);
		return {
			...post,
			reactions: {
				count: nextCount,
				reacted: false,
				type: null,
				types: nextCount === 0 ? [] : types,
			},
		};
	}

	return {
		...post,
		reactions: {
			count: reacted ? count : count + 1,
			reacted: true,
			type,
			types: withType(types, type),
		},
	};
}

export function setPostReaction(post: Post, reactions: PostReactions): Post {
	return { ...post, reactions };
}

export function bumpCommentCount(post: Post, delta: number): Post {
	return { ...post, commentCount: Math.max(0, post.commentCount + delta) };
}

export interface CommentViewer {
	userId?: string;
	admin: boolean;
	role?: string;
}

export function canDeleteComment(
	comment: Comment,
	post: Post,
	viewer: CommentViewer,
): boolean {
	if (!viewer.userId) return false;
	if (comment.author.id === viewer.userId) return true;
	if (viewer.admin) return true;
	if (post.author.id === viewer.userId) return true;
	return viewer.role?.trim().toLowerCase() === "engineer";
}

export function canModeratePost(post: Post, viewer: CommentViewer): boolean {
	if (!viewer.userId) return false;
	if (post.author.id === viewer.userId) return true;
	if (viewer.admin) return true;
	return viewer.role?.trim().toLowerCase() === "engineer";
}

export function isPostEdited(post: Post): boolean {
	return Date.parse(post.updatedAt) > Date.parse(post.createdAt);
}
